/**
 * THE TWO CLIENTS NEVER AGREED ON WHERE ATTACHMENTS LIVE.
 *
 * ── THE MISMATCH ────────────────────────────────────────────────────────────────────────────────
 *
 *   this client   `attachment`  (SINGULAR)  [ { "<fileName>": "<s3 path>" } ]
 *   mobile        `attachments` (PLURAL)    [ { name, path, type, version, size } ]
 *
 * Both are written INSIDE the sealed body, so neither side can migrate the other's existing mail.
 * The consequence was symmetrical and completely silent: a file sent from the phone was invisible
 * here — the message opened, the text rendered, and the attachment strip was simply absent, with no
 * error and nothing to suggest anything had been left out. The phone had the same blind spot in
 * reverse until it learned to read this client's shape.
 *
 * The phone reads both now. This is the other half.
 *
 * ── WHY IT NORMALISES TO THE SINGULAR SHAPE ─────────────────────────────────────────────────────
 *
 * Every consumer in this app — the icon lookup, the preview branch, the download handler, the render
 * — is written against `Object.keys(entry)[0]` and `entry[thatKey]`. Rewriting all of them to a
 * richer record would be a large change to the most load-bearing screen in the client, for fields it
 * does not use:
 *
 *   `type`     already derived from the filename extension at every one of those sites.
 *   `version`  NOT taken from this record by either client. Both read it from the STORED object's
 *              own JSON wrapper (`{ name, version, data }`), which is the only copy that cannot be
 *              stale — so dropping it here costs nothing and removes a way for the two to disagree.
 *   `size`     display only, and this client has never shown one.
 *
 * So the plural shape is folded into the singular one at the door, and everything downstream stays
 * exactly as it was.
 *
 * ── PLURAL WINS WHERE A MESSAGE CARRIES BOTH ────────────────────────────────────────────────────
 *
 * The server emits both keys for inbound internet mail. The plural one is the richer record and the
 * one that can mark a file unavailable, so it is preferred — and reading both is strictly additive:
 * no message that renders today renders differently.
 */

/**
 * Is this entry the shape this client already understands?
 *
 * One key, mapping a filename to a stored path. Checked rather than assumed because a malformed
 * entry reaching `Object.keys(entry)[0]` yields `undefined` and renders as an attachment named
 * "undefined" that downloads nothing.
 */
const isLegacyEntry = (entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
    const keys = Object.keys(entry);
    if (keys.length !== 1) return false;
    return typeof entry[keys[0]] === "string" && entry[keys[0]].length > 0;
};

/** Fold one of the phone's records into `{ "<fileName>": "<path>" }`, or null if it cannot be used. */
const fromPlural = (entry) => {
    if (!entry || typeof entry !== "object") return null;

    const name = String(entry.name ?? "").trim();
    const path = String(entry.path ?? "").trim();
    if (!name || !path) return null;

    /**
     * A FILE THAT EXISTS ONLY AS A NAME IS SKIPPED.
     *
     * Mail that arrived from the public internet before the server started storing attachments has
     * the filename and no bytes, and says so with `available: false`. Rendering it as an ordinary
     * row would give the reader a download button that fails every time. Showing nothing is not
     * ideal either, but it is what this client does today for a message it cannot serve, and a
     * failing button is worse than an absence.
     */
    if (entry.available === false) return null;

    return { [name]: path };
};

/**
 * Every attachment on a decrypted message, in the shape this client renders.
 *
 * Always an array — a message with none, a payload that is a bare string, and a malformed record
 * all give `[]`, because every caller does the same thing with all three.
 */
export function readAttachmentList(payload) {
    if (!payload || typeof payload !== "object") return [];

    if (Array.isArray(payload.attachments) && payload.attachments.length) {
        const folded = payload.attachments.map(fromPlural).filter(Boolean);
        // Only fall through when the plural list yielded nothing usable — a message whose files are
        // all unavailable should not silently fall back to a stale singular list.
        if (folded.length) return folded;
        if (!Array.isArray(payload.attachment)) return [];
    }

    if (Array.isArray(payload.attachment)) {
        return payload.attachment.filter(isLegacyEntry);
    }

    return [];
}

export default readAttachmentList;
